import { FC, useEffect, useMemo, useState } from "react";
import { idmService, IIdmStaffListItem } from "../../../entities/idm";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import { IShift, shiftService } from "../../../entities/shift";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { MyError } from "../../../shared/lib/error/MyError";
import { IItem } from "../../../shared/model/types";
import { DropDownListSelected } from "../../../shared/ui/dropDownSelected";
import { Modal } from "../../../shared/ui/modal";
import { ConfirmationAction } from "../../../shared/ui/confirmationAction";
import { LoaderSpinner } from "../../../shared/ui/spinner";
import { MyTextarea } from "../../../shared/ui/myTextarea";
import { MyButton, MyButtonSecondary } from "../../../shared/ui/button";
import classes from './shiftReplaceStaff.module.scss'

export type TShiftReplaceStaffMode = 'coordinator' | 'curator'

interface IProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    mode: TShiftReplaceStaffMode;
    shift: IShift;
    teamId: string;
    teamName: string;
    workTgIds?: number[];
    tgAccountId?: string;
    tgAccountName?: string;
    currentUserId?: string;
    curatorReplacementOptions?: IItem[];
    coordinatorReplacementOption?: IItem;
    onSuccess: (shift: IShift) => void;
}

const staffToItem = (staff: IIdmStaffListItem): IItem => ({
    id: staff.user_id,
    name: `${staff.user_id}. ${staff.full_name}`.trim(),
})

const uniqueItems = (items: IItem[]): IItem[] => {
    const map = new Map<number | string, IItem>()
    items.forEach(item => {
        if(item.id) map.set(item.id, item)
    })

    return Array.from(map.values())
}

export const ShiftReplaceStaff: FC<IProps> = ({
    open,
    setOpen,
    mode,
    shift,
    teamId,
    teamName,
    workTgIds = [],
    tgAccountId,
    tgAccountName,
    currentUserId,
    curatorReplacementOptions,
    coordinatorReplacementOption,
    onSuccess,
}) => {

    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()

    const [staffOptions, setStaffOptions] = useState<IItem[]>([])
    const [replacementUserId, setReplacementUserId] = useState<number>(0)
    const [comment, setComment] = useState<string>('')
    const [error, setError] = useState<string>('')
    const [isLoadingOptions, setIsLoadingOptions] = useState<boolean>(false)
    const [isSaving, setIsSaving] = useState<boolean>(false)
    const [confirm, setConfirm] = useState<boolean>(false)

    const title = mode === 'coordinator' ? 'Подмена координатора' : 'Подмена куратора'
    const targetLabel = mode === 'coordinator'
        ? teamName
        : `${teamName}${tgAccountName ? ` · ${tgAccountName}` : ''}`

    const currentUserIdNumber = useMemo(() => Number(currentUserId ?? 0), [currentUserId])
    const workTgIdsKey = useMemo(() => workTgIds.join(','), [workTgIds])
    const selectedIds = replacementUserId ? [replacementUserId] : []

    const handleError = (e: unknown, defaultMessage: string) => {
        console.log(e)

        if(e instanceof AuthError){
            setIsAuth(false)
            setGlobalMessage({message: e.message, type: 'error'})
            return
        }

        if(e instanceof MyError){
            setGlobalMessage({message: e.message, type: 'error'})
            return
        }

        setGlobalMessage({message: defaultMessage, type: 'error'})
    }

    useEffect(() => {
        if(!open) return

        let isActual = true

        const getOptions = async () => {
            try{
                setIsLoadingOptions(true)
                setError('')
                setComment('')
                setConfirm(false)
                setReplacementUserId(0)

                if(!isActual) return

                let options: IItem[]
                if(mode === 'coordinator'){
                    const roles = await idmService.listRoles()
                    const headTeacherRoleId = roles.find(role => role.code === 'assistant')?.id
                    const [coordinatorsResponse, headTeachersResponse] = await Promise.all([
                        idmService.listAvailableCoordinators(workTgIds),
                        headTeacherRoleId ? idmService.listStaff([headTeacherRoleId]) : Promise.resolve({items: [], total: 0}),
                    ])

                    options = uniqueItems([
                        ...coordinatorsResponse.items.map(staffToItem),
                        ...headTeachersResponse.items.map(staffToItem),
                    ]).filter(item => Number(item.id) !== currentUserIdNumber)
                }
                else {
                    options = (curatorReplacementOptions
                        ?? (await idmService.listAvailableCurators(tgAccountId ? [Number(tgAccountId)] : [])).items.map(staffToItem)
                    ).filter(item => Number(item.id) !== currentUserIdNumber)
                }

                if(!isActual) return

                if(mode === 'curator' && coordinatorReplacementOption){
                    const alreadyExists = options.some(item => item.id === coordinatorReplacementOption.id)
                    const isCurrentUser = Number(coordinatorReplacementOption.id) === currentUserIdNumber
                    if(!alreadyExists && !isCurrentUser){
                        options.push(coordinatorReplacementOption)
                    }
                }

                setStaffOptions(options.sort((a, b) => a.name.localeCompare(b.name)))
            }
            catch(e){
                handleError(e, 'Ошибка при получении сотрудников для подмены')
            }
            finally{
                if(isActual) setIsLoadingOptions(false)
            }
        }

        getOptions()

        return () => {
            isActual = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, mode, tgAccountId, currentUserIdNumber, workTgIdsKey, curatorReplacementOptions, coordinatorReplacementOption])

    const onClose = (value: boolean) => {
        if(!value){
            setConfirm(false)
            setError('')
        }
        setOpen(value)
    }

    const onPrepareConfirm = () => {
        if(!replacementUserId){
            setError('Выберите сотрудника для подмены')
            return
        }

        if(!comment.trim()){
            setError('Укажите причину подмены')
            return
        }

        setError('')
        setConfirm(true)
    }

    const onSubmit = async () => {
        try{
            setIsSaving(true)

            if(mode === 'curator' && !tgAccountId){
                setError('Не найден рабочий Telegram для подмены куратора')
                return
            }

            const response = mode === 'coordinator'
                ? await shiftService.replaceShiftCoordinator({
                    shiftId: shift.id,
                    teamId,
                    replacementUserId: String(replacementUserId),
                    comment: comment.trim(),
                })
                : await shiftService.replaceShiftCurator({
                    shiftId: shift.id,
                    teamId,
                    tgAccountId: tgAccountId || '',
                    replacementUserId: String(replacementUserId),
                    comment: comment.trim(),
                })

            setGlobalMessage({message: 'Подмена сохранена', type: 'ok'})
            onClose(false)
            onSuccess(response.shift)
        }
        catch(e){
            handleError(e, 'Ошибка при сохранении подмены')
        }
        finally{
            setIsSaving(false)
        }
    }

    return (
        <Modal open={open} setOpen={onClose}>
            {confirm ? (
                <ConfirmationAction
                    title="Точно сохранить подмену?"
                    type="send"
                    setOpen={setConfirm}
                    onClick={onSubmit}
                    isLoading={isSaving}
                />
            ) : (
                <section className={classes.container}>
                    <section className={classes.header}>
                        <section>
                            <h3>{title}</h3>
                            <p>{targetLabel}</p>
                        </section>
                        <button type="button" className={classes.closeButton} onClick={() => onClose(false)}>×</button>
                    </section>

                    {isLoadingOptions ? (
                        <section className={classes.loader}>
                            <LoaderSpinner />
                        </section>
                    ) : (
                        <section className={classes.form}>
                            <section className={classes.field}>
                                <span className={classes.label}>Кто заменяет</span>
                                <DropDownListSelected
                                    items={staffOptions}
                                    selectedIdItems={selectedIds}
                                    onSelected={(item) => (selected) => {
                                        setReplacementUserId(selected ? Number(item.id) : 0)
                                        setError('')
                                    }}
                                    isLoading={false}
                                    search
                                    placeholder="Выберите сотрудника"
                                    searchPlaceholder="Поиск сотрудника"
                                    emptyText="Нет доступных сотрудников"
                                />
                            </section>

                            <MyTextarea
                                title="Причина подмены"
                                value={comment}
                                setValue={setComment}
                                setError={setError}
                                placeholder="Например: сотрудник не может выйти на смену"
                                rows={5}
                            />

                            {error && <section className={classes.error}>*{error}</section>}
                        </section>
                    )}

                    <section className={classes.footer}>
                        <section className={classes.secondaryButton}>
                            <MyButtonSecondary onClick={() => onClose(false)}>
                                Отмена
                            </MyButtonSecondary>
                        </section>
                        <section className={classes.submitButton}>
                            <MyButton
                                onClick={onPrepareConfirm}
                                isLoading={isLoadingOptions}
                            >
                                Сохранить подмену
                            </MyButton>
                        </section>
                    </section>
                </section>
            )}
        </Modal>
    )
}
